import { useState } from 'react';
import axios from 'axios';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type { PaginationState } from '~/hooks/useIdBasedPagination';
import useIdBasedPagination from '~/hooks/useIdBasedPagination';
import { reportAxiosError } from '~/Utils';

import {
  sorIssueIdsUrl,
  sorIssueOperationsUrl,
  sorIssuesByIdsUrl,
  sorReconcileUrl,
  sorResolveAttemptUrl,
} from '../api';
import type {
  SorIssue,
  SorIssueIdsParams,
  SorIssueStatusCounts,
  SorOperation,
  SorOperationsResponse,
  SorReconcileReport,
  SorResolveAttemptParams,
} from '../types';
import { SorIssueFilterStatus } from '../types';

const ROWS_PER_PAGE = 25;

export interface SorIssuesFilters {
  status: SorIssueFilterStatus[];
  operation: string;
  claimNumber: string;
  /** A local day, YYYY-MM-DD; empty for none. */
  startDate: string;
  endDate: string;
}

// The tab opens on the attempts a human may need to act on: the open ones, and those the reconciler gave up on.
const DEFAULT_FILTERS: SorIssuesFilters = {
  status: [SorIssueFilterStatus.NeedsAttention, SorIssueFilterStatus.Pending, SorIssueFilterStatus.RemoteDone],
  operation: '',
  claimNumber: '',
  startDate: '',
  endDate: '',
};

const NO_FILTERS: SorIssuesFilters = { status: [], operation: '', claimNumber: '', startDate: '', endDate: '' };

// The server compares naive UTC start times inclusively, at the microsecond precision of the column, so a local day
// is sent as its first and last microsecond in UTC. moment stops at milliseconds, so the last three digits are written.
const toServerDayStart = (localDay: string): string =>
  moment(localDay).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000]');
const toServerDayEnd = (localDay: string): string =>
  moment(localDay).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[999]');

const toIssueIdsParams = ({
  status,
  operation,
  claimNumber,
  startDate,
  endDate,
}: SorIssuesFilters): SorIssueIdsParams => ({
  ...(!isEmpty(status) && { status }),
  ...(operation && { operation }),
  ...(claimNumber && { claim_number: claimNumber }),
  ...(startDate && { iso_datetime_from: toServerDayStart(startDate) }),
  ...(endDate && { iso_datetime_to: toServerDayEnd(endDate) }),
});

interface UseSorIssuesReturnType {
  filters: SorIssuesFilters;
  /** Filters again from the first page */
  updateFilters: (update: Partial<SorIssuesFilters>) => void;
  clearFilters: () => void;
  operations: SorOperation[];
  issues: SorIssue[];
  /** How many attempts each status would list under the other filters; undefined until the first list loads */
  statusCounts: SorIssueStatusCounts['status_counts'] | undefined;
  pagination: PaginationState;
  isLoading: boolean;
  isError: boolean;
  reload: () => void;
  isReconciling: boolean;
  /** Calls reportAxiosError and rethrows on error */
  reconcile: () => Promise<SorReconcileReport>;
  /** Rethrows without reporting: the resolve form shows the server's refusal itself, and reports any other error */
  resolveAttempt: (attemptId: number, params: SorResolveAttemptParams) => Promise<SorIssue>;
}

export const useSorIssues = (): UseSorIssuesReturnType => {
  const { organization } = useSysconfig() as { organization: { id: number } };
  const [filters, setFilters] = useState<SorIssuesFilters>(DEFAULT_FILTERS);
  const [isReconciling, setIsReconciling] = useState(false);

  const operationsFetcher = useDataFetcher<SorOperationsResponse>(sorIssueOperationsUrl(organization.id));
  const { pagination, pageResults, extraData, isLoading, isError, reload } = useIdBasedPagination<
    number,
    SorIssueStatusCounts,
    SorIssue,
    null
  >({
    fetchIdsUrl: sorIssueIdsUrl(organization.id),
    fetchPageDataUrl: sorIssuesByIdsUrl(organization.id),
    fetchIdsParams: toIssueIdsParams(filters) as unknown as Record<string, unknown>,
    initialRowsPerPage: ROWS_PER_PAGE,
  });

  // The pagination keeps its page when the ids change, which can leave it past the end of a shorter list.
  const updateFilters = (update: Partial<SorIssuesFilters>) => {
    setFilters((current) => ({ ...current, ...update }));
    pagination.setPage(0);
  };

  const clearFilters = () => {
    setFilters(NO_FILTERS);
    pagination.setPage(0);
  };

  const reconcile = async () => {
    try {
      setIsReconciling(true);
      const { data } = await axios.post<SorReconcileReport>(sorReconcileUrl(organization.id));
      reload();
      return data;
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    } finally {
      setIsReconciling(false);
    }
  };

  const resolveAttempt = async (attemptId: number, params: SorResolveAttemptParams) => {
    const { data } = await axios.post<SorIssue>(sorResolveAttemptUrl(organization.id, attemptId), params);
    reload();
    return data;
  };

  return {
    filters,
    updateFilters,
    clearFilters,
    operations: operationsFetcher.data?.operations ?? [],
    issues: pageResults,
    statusCounts: extraData?.status_counts,
    pagination,
    isLoading,
    isError,
    reload,
    isReconciling,
    reconcile,
    resolveAttempt,
  };
};
